import React from "react";
import { usePlacesWidget } from "react-google-autocomplete";

interface GooglePlacesAutocompleteProps {
	value: string;
	onChange: (address: string) => void;
	placeholder?: string;
	className?: string;
	onPlaceSelect?: (place: google.maps.places.PlaceResult) => void;
}

const GooglePlacesAutocomplete: React.FC<GooglePlacesAutocompleteProps> = ({
	value,
	onChange,
	placeholder = "Enter address",
	className = "",
	onPlaceSelect,
}) => {
	const { ref } = usePlacesWidget({
		apiKey: process.env.REACT_APP_GOOGLE_API_KEY || "",
		options: {
			types: [], // allow all address/POI types so more results can show
			fields: [
				"formatted_address",
				"address_components",
				"geometry",
				"name",
				"place_id",
			],
		},
		onPlaceSelected: (addr) => {
			if (!addr) return;

			const place = addr.address_components;
			if (!place) return;

			// Extract address components into a more usable format
			const address = place.reduce(
				(
					seed: any,
					{ long_name, types }: { long_name: string; types: string[] }
				) => {
					types.forEach((t) => (seed[t] = long_name));
					return seed;
				},
				{}
			);

			const formattedAddress = addr.formatted_address || "";
			onChange(formattedAddress);

			// Convert to PlaceResult format for backward compatibility
			if (onPlaceSelect) {
				const placeResult: google.maps.places.PlaceResult = {
					formatted_address: formattedAddress,
					address_components: place,
					geometry: addr.geometry,
					name: addr.name || "",
					place_id: addr.place_id || "",
				};
				onPlaceSelect(placeResult);
			}
		},
	});

	// Handle manual input changes
	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		onChange(e.target.value);
	};

	return (
		<input
			ref={ref as unknown as React.RefObject<HTMLInputElement>}
			type='text'
			value={value}
			onChange={handleInputChange}
			placeholder={placeholder}
			className={className}
		/>
	);
};

export default GooglePlacesAutocomplete;
